import { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useConnections } from '@/core/connection/connection.context'
import { useConnectors } from '@/core/hooks'
import { useAuth } from '@/core/auth/auth.context'
import { ThemeToggle } from '@/shared/components/theme'
import { Icon } from '@/shared/components/icons'

/** Where the collapsed/expanded choice is remembered, per browser. */
const RAIL_KEY = 'shell.sidebar.collapsed'

export function AppShell() {
  const { connections } = useConnections()
  const catalog = useConnectors()
  const { user, loginRequired, signOut } = useAuth()

  // Collapsed to an icon rail rather than hidden: on a laptop the sidebar is
  // worth ~170px of the graph canvas, but losing navigation entirely to get it
  // back is a bad trade. Remembered in a try/catch because storage throws
  // outright in some privacy modes.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) === '1'
    } catch {
      return false
    }
  })
  function toggle() {
    setCollapsed((current) => {
      const next = !current
      try {
        localStorage.setItem(RAIL_KEY, next ? '1' : '0')
      } catch {
        // A remembered preference is a nicety; losing it is not an error.
      }
      return next
    })
  }

  const unhealthy = connections.filter((c) => c.status !== 'active').length
  const RailIcon = collapsed ? Icon.expand : Icon.collapse

  return (
    <div className="shell" data-collapsed={collapsed}>
      <a className="skip-link" href="#main">Skip to content</a>
      <aside className="sidebar" aria-label="Primary">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            <Icon.graph />
          </span>
          <span className="navtext brand-wordmark">context</span>
          {/* Stays in the rail when collapsed, so the way back is always the
              same control in the same place. */}
          <button
            type="button"
            className="railtoggle"
            onClick={toggle}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
            title={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
          >
            <RailIcon />
          </button>
        </div>

        {/* Each link carries an icon so the collapsed rail is navigable rather
            than a column of anonymous boxes. `title` gives it a tooltip there,
            where the label itself is not rendered. */}
        <nav className="nav" aria-label="Sections">
          <span className="nav-label">Workspace</span>
          <NavLink to="/catalog" title="Connectors">
            <Icon.plug className="navicon" />
            <span className="navtext">Connectors</span>
            {catalog.data && (
              <span className="badge badge-neutral">{catalog.data.connectors.length}</span>
            )}
          </NavLink>
          <NavLink to="/connections" title="Connections">
            <Icon.link className="navicon" />
            <span className="navtext">Connections</span>
            {connections.length > 0 && (
              <span className={`badge ${unhealthy ? 'badge-warn' : 'badge-neutral'}`}>
                {connections.length}
              </span>
            )}
          </NavLink>
          <NavLink to="/run" title="Run an action">
            <Icon.play className="navicon" />
            <span className="navtext">Run</span>
          </NavLink>
          <NavLink to="/executions" title="History">
            <Icon.history className="navicon" />
            <span className="navtext">History</span>
          </NavLink>
          <NavLink to="/workflows" title="Workflows">
            <Icon.workflow className="navicon" />
            <span className="navtext">Workflows</span>
          </NavLink>

          <span className="nav-label" style={{ paddingTop: 16 }}>
            Knowledge
          </span>
          <NavLink to="/brain" title="Knowledge graph">
            <Icon.brain className="navicon" />
            <span className="navtext">Knowledge graphs</span>
          </NavLink>
          <NavLink to="/explorer" title="Explorer">
            <Icon.compass className="navicon" />
            <span className="navtext">Explorer</span>
          </NavLink>
          <NavLink to="/objects" title="Objects">
            <Icon.table className="navicon" />
            <span className="navtext">Objects</span>
          </NavLink>
          <NavLink to="/agents" title="Agents">
            <Icon.bot className="navicon" />
            <span className="navtext">Agents</span>
          </NavLink>
          <NavLink to="/chat" title="Chat with a model">
            <Icon.chat className="navicon" />
            <span className="navtext">Chat</span>
          </NavLink>
        </nav>

        <div className="spacer" />

        <div className="stack sidebar-foot" style={{ gap: 4 }}>
          {user && (
            <div className="workspace-profile" title={user.display_name || user.username}>
              <span className="profile-avatar" aria-hidden="true">
                {(user.display_name || user.username).slice(0, 2).toUpperCase()}
              </span>
              <div className="navtext">
                <strong>{user.display_name || user.username}</strong>
                <span>{user.is_admin ? 'Administrator' : 'Workspace member'}</span>
              </div>
            </div>
          )}
          {/* Anonymous access is only possible when no accounts exist at all.
              Saying so is important: it looks identical to being signed in.
              A paragraph of warning cannot be read in a 64px rail, and
              truncating it would hide the one thing it exists to say. */}
          {!loginRequired && !collapsed && (
            <div className="callout callout-warn" style={{ padding: '9px 11px', marginBottom: 4 }}>
              <p className="small" style={{ margin: 0 }}>
                Open access — no accounts exist yet, so anyone who can reach this
                server can use it.
              </p>
            </div>
          )}

          {/* It already knows how to drop its own label. */}
          <ThemeToggle compact={collapsed} />

          {user && (
            <button
              className="btn btn-ghost"
              onClick={() => void signOut()}
              style={{ justifyContent: 'flex-start' }}
              title={`Sign out (${user.display_name || user.username})`}
            >
              <Icon.signOut className="navicon" />
              <span className="truncate navtext">
                Sign out
              </span>
            </button>
          )}
        </div>
      </aside>

      <div className="workspace-content">
        <main className="main" id="main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
